import type { InsightTodayPageViewContext } from '@/types/insights';

/**
 * 未閲覧の発想のヒントがあったかは、遷移先で開いた時点で既読になるためクリック時に記録するしかない。
 * グローバルナビゲーションの項目は router-link の navigate を使わない素の a 要素で、
 * hashchange で遷移するため router.push の state では遷移先へ渡せない。
 * リリース後にチャンクの hash が変わると main.ts が vite:preloadError で全体をリロードするため、
 * モジュール変数ではなく、リロードを越えて残る sessionStorage を使う。
 * 遷移先で読んだら消して、後のリロードで再送しないようにする。
 */
const STORAGE_KEY = 'insights.todayEntry';

const isPlainLeftClick = (event: MouseEvent): boolean =>
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey &&
  !event.defaultPrevented;

export const recordInsightTodayEntry = (
  event: MouseEvent,
  hadUnviewedInsight: boolean,
): void => {
  // 別タブで開くクリックは値が今のタブに残り、後の別の訪問に紐づいてしまうため記録しない。
  if (!isPlainLeftClick(event)) return;
  const context: InsightTodayPageViewContext = {
    entry_point: 'global_navigation',
    had_unviewed_insight: hadUnviewedInsight,
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  } catch {
    // 計測のために遷移を妨げないよう、保存できない環境では記録を諦める。
  }
};

const isInsightTodayPageViewContext = (
  value: unknown,
): value is InsightTodayPageViewContext =>
  typeof value === 'object' &&
  value !== null &&
  'entry_point' in value &&
  value.entry_point === 'global_navigation' &&
  'had_unviewed_insight' in value &&
  typeof value.had_unviewed_insight === 'boolean';

export const consumeInsightTodayEntry = ():
  | InsightTodayPageViewContext
  | undefined => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored === null) return undefined;
    sessionStorage.removeItem(STORAGE_KEY);
    const parsed: unknown = JSON.parse(stored);
    return isInsightTodayPageViewContext(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};
